import React from 'react';
import { AlertCircle, RotateCcw, X, CloudOff } from 'lucide-react';

/**
 * Restore Draft Prompt - Modal that appears when unsaved work is detected
 */
export function RestoreDraftPrompt({ draft, onRestore, onDismiss }) {
  if (!draft) return null;

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="restore-draft-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-amber-50 border-b border-amber-100 px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <CloudOff className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Unsaved Work Found</h3>
            <p className="text-sm text-slate-500">Last edited {formatTime(draft.timestamp)}</p>
          </div>
        </div>
        
        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-sm text-slate-600 mb-4">
            We found an unsaved quote draft. Would you like to restore it?
          </p>
          
          {/* Draft Preview */}
          <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              {draft.clientName && (
                <div>
                  <span className="text-slate-500">Client:</span>{' '}
                  <span className="font-medium text-slate-700">{draft.clientName}</span>
                </div>
              )}
              {draft.currentProvider && (
                <div>
                  <span className="text-slate-500">Provider:</span>{' '}
                  <span className="font-medium text-slate-700">{draft.currentProvider}</span>
                </div>
              )}
              {draft.lineItems?.length > 0 && (
                <div>
                  <span className="text-slate-500">Line Items:</span>{' '}
                  <span className="font-medium text-slate-700">{draft.lineItems.length}</span>
                </div>
              )}
              {draft.preparedBy && (
                <div>
                  <span className="text-slate-500">Prepared By:</span>{' '}
                  <span className="font-medium text-slate-700">{draft.preparedBy}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex gap-3 justify-end">
          <button
            onClick={onDismiss}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="dismiss-restore-btn"
          >
            Start Fresh
          </button>
          <button
            onClick={onRestore}
            className="px-4 py-2 text-sm font-medium text-white bg-[#3c68ff] hover:bg-[#2b4bca] rounded-lg transition-colors flex items-center gap-2"
            data-testid="restore-draft-btn"
          >
            <RotateCcw className="w-4 h-4" />
            Restore Draft
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Unsaved Changes Indicator - Shows in header when there are unsaved changes
 */
export function UnsavedIndicator({ visible }) {
  if (!visible) return null;
  
  return (
    <div 
      className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-full text-xs font-medium text-amber-700"
      data-testid="unsaved-indicator"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
      Unsaved
    </div>
  );
}
